import { SellerFilterOption } from '@/components/SellerFilter/SellerFilterUi';
import { useGetOrderDetailsFilters } from '@/hooks/order/useGetOrderDetailsFilters';
import { useGetKamList } from '@/hooks/staff/useGetKamList';
import { kamListResponseToKamOptionUiList } from '@/hooks/staff/useGetKamList/mapper';
import { DEFER_SEARCH_TIMEOUT } from '@/utils/constants';
import { isString } from '@/utils/functions';
import { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { FilterUi, initialState } from './FilterUi';

let searchTimeout: NodeJS.Timeout;

export function useFiltersViewController(
  onFilterChanged: (newFilter: FilterUi) => void,
  onlyPendingOrders: boolean
) {
  const [uiState, setUiState] = useImmer<FilterUi>(initialState);
  const [isDeferingSearch, setIsDeferringSearch] = useState(false);
  const { data: kamListData } = useGetKamList();

  const { data: statusListData } = useGetOrderDetailsFilters();

  useEffect(() => {
    if (!kamListData) return;
    setUiState((draft) => {
      draft.kamFilterOptions = kamListResponseToKamOptionUiList(kamListData);
    });
  }, [kamListData]);

  useEffect(() => {
    if (!statusListData) return;
    setUiState((draft) => {
      draft.statusOptions = statusListData.order_status_filters.map((item) => ({
        statusId: item.internal_name,
        statusName: item.display_name,
      }));
    });
  }, [statusListData]);

  useEffect(() => {
    setUiState((draft) => {
      draft.orderId = '';
      draft.selectedKamIds = [];
      draft.selectedStatusId = onlyPendingOrders ? 'accepted' : '';
      draft.remissionStatusId = onlyPendingOrders ? 'true' : '';
    });
  }, [onlyPendingOrders]);

  const onOrderIdChanged = (newValue: string) => {
    setIsDeferringSearch(true);
    clearTimeout(searchTimeout);
    setUiState((draft) => {
      draft.orderId = newValue.trim();
      draft.startDate = undefined;
      draft.endDate = undefined;
    });
    searchTimeout = setTimeout(() => {
      setIsDeferringSearch(false);
    }, DEFER_SEARCH_TIMEOUT);
  };

  const cleanOrderId = () =>
    setUiState((draft) => {
      draft.orderId = '';
    });

  const cleanDateRange = () =>
    setUiState((draft) => {
      draft.startDate = undefined;
      draft.endDate = undefined;
    });

  const updateDateRange = (updatedValue: [Date | null, Date | null]) =>
    setUiState((draft) => {
      draft.startDate = updatedValue[0] ?? undefined;
      draft.endDate = updatedValue[1] ?? undefined;
      draft.orderId = '';
    });

  const onSellerFilterChanged = (selected: SellerFilterOption[]) =>
    setUiState((draft) => {
      draft.sellers = selected;
      draft.orderId = '';
    });

  const onStatusFilterChanged = (statusId: string) =>
    setUiState((draft) => {
      draft.selectedStatusId = statusId;
      draft.orderId = '';
    });

  const onStatusFilterClearClicked = () =>
    setUiState((draft) => {
      draft.selectedStatusId = '';
    });

  const onRemissionFilterChanged = (statusId: string) =>
    setUiState((draft) => {
      draft.remissionStatusId = statusId;
    });

  const onRemissionFilterClearClicked = () =>
    setUiState((draft) => {
      draft.remissionStatusId = '';
    });

  const onKamFilterChanged = (options: string | string[]) => {
    if (isString(options)) return;
    setUiState((draft) => {
      draft.selectedKamIds = options;
      draft.orderId = '';
    });
  };

  const onKamFilterClearClicked = () =>
    setUiState((draft) => {
      draft.selectedKamIds = [];
    });

  useEffect(() => {
    if (isDeferingSearch) return;
    if (uiState.startDate && !uiState.endDate) return; // is choosing date range
    onFilterChanged(uiState);
  }, [uiState, isDeferingSearch]);

  const kamsSelectedValue = uiState.kamFilterOptions
    .filter((option) => uiState.selectedKamIds.includes(option.id))
    .map((option) => option.name)
    .join(', ');

  const statusSelectedValue = uiState.statusOptions.find(
    (item) => item.statusId === uiState.selectedStatusId
  )?.statusName;

  const remissionStatusSelectedValue = uiState.remisionStatusOptions.find(
    (item) => item.statusId === uiState.remissionStatusId
  )?.statusName;

  return {
    uiState: {
      ...uiState,
      showClearDateRange: uiState.startDate && uiState.endDate,
      showClearOrderId: !!uiState.orderId.length,
      showClearKamFilter: !!uiState.selectedKamIds.length,
      showClearStatusFilter: !!uiState.selectedStatusId.trim(),
      showClearRemissionStatusFilter: !!uiState.remissionStatusId,
      showStatusFilter: !onlyPendingOrders,
      statusSelectedValue,
      showRemissionStatusFilter: !onlyPendingOrders,
      remissionStatusSelectedValue,
      kamsSelectedValue,
    },
    actions: {
      onOrderIdChanged,
      cleanOrderId,
      updateDateRange,
      cleanDateRange,
      onSellerFilterChanged,
      onStatusFilterChanged,
      onStatusFilterClearClicked,
      onRemissionFilterChanged,
      onRemissionFilterClearClicked,
      onKamFilterChanged,
      onKamFilterClearClicked,
    },
  };
}
